import { FC } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { EmptyCircleIcon } from '@/atoms/Icons/EmptyCircleIcon'
import { GuildShield } from '@/atoms/Icons/GuildShield'
import { PersonIcon } from '@/atoms/Icons/PersonIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { CaptionSM, CaptionXS, HeadingLG, ParagraphSM, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useUser } from '@/services/UserService'
import { useSafeAnalytics } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { WatchBackground } from '@/views/WatchLandingView/WatchBackground'

interface PerkProps {
  id: number
  title: string
  list: 'freePlan' | 'guildPlan' | 'both' | string
}
interface PerksCardProps {
  list: 'freePlan' | 'guildPlan'
  perks: PerkProps[]
  icon: React.ReactNode
  title: string
  subtitle: string
  cta: string
  path: string
  disclaimer?: string
  onClick?: () => void
}

export const SignUpPickerView = () => {
  const { t } = useTranslate('sign-up')
  const { isLoggedIn } = useUser()
  const { loginUrl, trackLoginStarted } = useLoginUrl(paths.index)

  const PerksList = [
    {
      id: 1,
      title: t('limitedAccess', '*Get limited access to our library of movies and TV shows'),
      list: 'freePlan',
    },
    {
      id: 2,
      title: t('joinPublicLivestreams', 'Join public livestreams with actors and filmmakers'),
      list: 'freePlan',
    },
    {
      id: 1,
      title: t('watchAllShows', 'Watch all of our shows'),
      list: 'guildPlan',
    },
    {
      id: 2,
      title: t('guildExclusiveLivestreams', 'Guild exclusive livestreams including Dry Bar LIVE recordings'),
      list: 'guildPlan',
    },
    {
      id: 8,
      title: t('engageWithCommunityV2', 'Engage with the Angel community by sharing your story'),
      list: 'both',
    },
    {
      id: 3,
      title: t('voteOnPilots', 'Vote on pilots, concepts, and feature films and decide what we should make next'),
      list: 'guildPlan',
    },
    {
      id: 4,
      title: t('earlyAccess', 'Early access to new releases'),
      list: 'guildPlan',
    },
    {
      id: 5,
      title: t('complimentaryTicketsV4', "Two complimentary tickets to every Angel' theatrical release"),
      list: 'guildPlan',
    },
    {
      id: 6,
      title: t('discountedMerch', 'Discounted merch in the Angel Store'),
      list: 'guildPlan',
    },
    {
      id: 7,
      title: t('reduceNegativeImpact', 'Help reduce the negative impact of media on children'),
      list: 'guildPlan',
    },
  ]

  return (
    <main className="lg:h-screen lg:overflow-y-hidden">
      <WatchBackground />
      <div className="my-3 hidden sm:mx-6 sm:my-4 md:flex md:justify-start">
        <InternalLink aria-label="Angel" href={paths.home}>
          <AngelLogo height={40} color="white" />
        </InternalLink>
      </div>
      <div className="relative mx-auto h-screen min-h-[550px] max-w-[789px] sm:px-8 lg:-mt-16 lg:flex lg:flex-col lg:justify-center lg:px-0">
        <section className="mx-auto mt-12 w-80 sm:mx-0 sm:w-auto">
          <HeadingLG weight="bold" color="white" className="md:photon-display md:text-[60px] lg:text-center">
            {t('chooseYourOwnAdventure', 'Choose your own adventure.')}
          </HeadingLG>
          <ParagraphSM
            color="core-gray-400"
            weight="medium"
            className="md:photon-paragraph-md lg:photon-paragraph-lg mt-2 lg:text-center"
          >
            {t(
              'guildSignupDescription',
              'Join the Guild to actively shape the future of entertainment, or create a free account to enjoy shows and movies picked by people like you.',
            )}
          </ParagraphSM>
        </section>
        <section className="mx-auto my-8 flex flex-wrap gap-8">
          <PerksCard
            list="guildPlan"
            perks={PerksList}
            icon={<GuildShield />}
            title={t('guildPlan', 'Guild Membership Plan')}
            subtitle={t('exclusiveAccess', 'Exlusive access to all Angel content')}
            cta={t('joinTheGuild', 'Join the Guild')}
            path={paths.guild.pricing}
          />
          <PerksCard
            list="freePlan"
            perks={PerksList}
            icon={<PersonIcon />}
            title={t('freePlan', 'Free Plan')}
            subtitle={t('noCreditCardRequired', 'No credit card required')}
            cta={isLoggedIn ? t('watchNow', 'Watch Now') : t('getStarted', 'Get Started')}
            path={isLoggedIn ? paths.watch.index : loginUrl}
            onClick={() => {
              if (!isLoggedIn) trackLoginStarted('SignUpPicker.PerksCard')
            }}
            disclaimer={t('earlyAccessNotAvailable', 'Early access to new releases not available.')}
          />
        </section>
      </div>
    </main>
  )
}

const PerksCard: FC<PerksCardProps> = ({ perks, icon, title, subtitle, cta, path, disclaimer, list, onClick }) => {
  const { track } = useSafeAnalytics()
  const availablePerks = perks.filter((perk) => perk.list === list || perk.list === 'both')

  return (
    <div className="mx-auto h-fit max-w-80 rounded-2xl bg-core-gray-100 p-6 backdrop-blur-xl">
      {icon}
      <TitleMD className="mt-2" weight="bold">
        {title}
      </TitleMD>
      <ParagraphSM color="core-gray-700">{subtitle}</ParagraphSM>
      <div className="my-4">
        {availablePerks?.map((perk) => (
          <Perk key={perk.id} perk={perk} active />
        ))}
      </div>
      <LinkButton
        href={path}
        variant="core-gray-900"
        className={classNames('w-full', {
          'lg:mt-[30px]': !disclaimer,
        })}
        onClick={() => {
          track(list === 'guildPlan' ? 'Sign Up Guild Plan Clicked' : 'Sign Up Free Plan Clicked')
          if (onClick) onClick()
        }}
      >
        {cta}
        <ArrowRightIcon size={16} />
      </LinkButton>
      <CaptionXS color="core-gray-700" className="my-2 text-center">
        {disclaimer ? (
          <>
            <sup>*</sup>
            {disclaimer}
          </>
        ) : (
          <>&nbsp;</>
        )}
      </CaptionXS>
    </div>
  )
}

const Perk = ({ perk, active }: { perk: PerkProps; active?: boolean }) => {
  const { title } = perk

  return (
    <div className="my-2 flex items-center">
      <div className="mr-2 h-4 w-4">
        {active ? (
          <CircleCheckIcon size={16} color1="oxide-primary" color2="white" />
        ) : (
          <EmptyCircleIcon size={16} color1="core-gray-500" color2="transparent" />
        )}
      </div>
      <CaptionSM color={active ? 'core-gray-950' : 'core-gray-600'}>{title}</CaptionSM>
    </div>
  )
}
